import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DollarSign, ArrowLeft, Pencil, Trash2, Building2, User, Calendar,
  Tag, Globe, Mail, Phone, Plus, X, TrendingUp, Target,
  FileText, ClipboardCheck, Shield, Layers, FolderOpen, Upload,
  Check, AlertTriangle, ChevronRight, BarChart3, ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useFundingStore } from '../store/useFundingStore';
import { fundingApi , downloadFile } from '../services/api';
import RefreshButton from '../components/common/RefreshButton';
import type {
  FundingOpportunity, FundingStage, FundingContact,
  BudgetLineItem, YearlyAllocation, ChecklistItem,
  ComplianceRequirement, Attachment,
} from '../types';

// ── Constants ─────────────────────────────────────────────────────

const STAGE_COLORS: Record<FundingStage, string> = {
  prospect: '#94a3b8', qualifying: '#3b82f6', preparing: '#8b5cf6',
  submitted: '#f59e0b', 'under-review': '#f97316', awarded: '#22c55e',
  declined: '#ef4444', withdrawn: '#6b7280',
};

const STAGE_LABELS: Record<FundingStage, string> = {
  prospect: 'Prospect', qualifying: 'Qualifying', preparing: 'Preparing',
  submitted: 'Submitted', 'under-review': 'Under Review', awarded: 'Awarded',
  declined: 'Declined', withdrawn: 'Withdrawn',
};

const FUNDER_TYPE_LABELS: Record<string, string> = {
  federal: 'Federal', state: 'State', local: 'Local',
  foundation: 'Foundation', corporate: 'Corporate',
  nonprofit: 'Nonprofit', international: 'International', other: 'Other',
};

const FUNDING_TYPE_LABELS: Record<string, string> = {
  grant: 'Grant', contract: 'Contract', 'cooperative-agreement': 'Cooperative Agreement',
  subgrant: 'Subgrant', sponsorship: 'Sponsorship', donation: 'Donation',
  loan: 'Loan', venture: 'Venture Capital', other: 'Other',
};

const BUDGET_CATEGORIES: Record<string, string> = {
  personnel: 'Personnel', fringe: 'Fringe Benefits', travel: 'Travel',
  equipment: 'Equipment', supplies: 'Supplies', contractual: 'Contractual',
  construction: 'Construction', consultants: 'Consultants', training: 'Training',
  evaluation: 'Evaluation', indirect: 'Indirect Costs', other: 'Other',
};

const COMPLIANCE_TYPES: Record<string, string> = {
  'quarterly-report': 'Quarterly Report', 'annual-report': 'Annual Report',
  'final-report': 'Final Report', 'financial-audit': 'Financial Audit',
  'site-visit': 'Site Visit', 'progress-update': 'Progress Update',
  'expenditure-report': 'Expenditure Report', closeout: 'Closeout', other: 'Other',
};

function fmt(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
}

function fmtDate(d: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function uid() { return Math.random().toString(36).slice(2, 10); }

// ── Main Component ────────────────────────────────────────────────

export default function FundingPage() {
  const { fundingId } = useParams<{ fundingId: string }>();
  const navigate = useNavigate();
  const { opportunities, fetchOpportunities, fetchSummary } = useFundingStore();

  useEffect(() => { fetchOpportunities(); fetchSummary(); }, []);

  if (fundingId) {
    const opp = opportunities.find((o) => o._id === fundingId);
    if (!opp) return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        <p>Opportunity not found. <button onClick={() => navigate('/funding')} className="text-accent hover:underline">Back to pipeline</button></p>
      </div>
    );
    return <FundingDetail opportunity={opp} />;
  }

  return <FundingPipelineBoard />;
}

// ── Pipeline Board ────────────────────────────────────────────────

function FundingPipelineBoard() {
  const { opportunities, summary, fetchOpportunities, fetchSummary } = useFundingStore();
  const navigate = useNavigate();
  const [showReports, setShowReports] = useState(false);

  const activeStages: FundingStage[] = ['prospect', 'qualifying', 'preparing', 'submitted', 'under-review'];
  const terminalStages: FundingStage[] = ['awarded', 'declined', 'withdrawn'];
  const byStage = (stage: FundingStage) => opportunities.filter((o) => o.stage === stage);

  if (showReports && summary) return <ReportsDashboard summary={summary} onBack={() => setShowReports(false)} />;

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Funding Pipeline</h1>
          <div className="flex items-center gap-2">
            <RefreshButton
              onRefresh={async () => { await Promise.all([fetchOpportunities(), fetchSummary()]); }}
              tooltip="Refresh funding data"
            />
            <button onClick={() => setShowReports(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              <BarChart3 size={14} /> Reports
            </button>
          </div>
        </div>
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Total Pipeline" value={fmt(summary.totalValue)} sub={`${summary.total} opportunities`} icon={<DollarSign size={18} />} color="text-blue-500" />
            <SummaryCard label="Weighted Value" value={fmt(summary.weightedValue)} sub="risk-adjusted" icon={<TrendingUp size={18} />} color="text-purple-500" />
            <SummaryCard label="Awarded" value={fmt(summary.awardedValue)} sub={`${summary.awarded} won · ${(summary as any).winRate ?? 0}% win rate`} icon={<Target size={18} />} color="text-green-500" />
            <SummaryCard label="Pending" value={fmt(summary.pendingValue)} sub={`${summary.pending} active`} icon={<Building2 size={18} />} color="text-amber-500" />
          </div>
        )}
      </div>

      <div className="flex-1 overflow-x-auto p-4">
        <div className="flex gap-3 min-w-max h-full">
          {activeStages.map((stage) => {
            const items = byStage(stage);
            const sv = items.reduce((s, o) => s + (o.amount || 0), 0);
            return (
              <div key={stage} className="w-64 flex flex-col bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{STAGE_LABELS[stage]}</span>
                    <span className="ml-auto text-xs text-gray-400 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">{items.length}</span>
                  </div>
                  {sv > 0 && <p className="text-xs text-gray-400 mt-0.5">{fmt(sv)}</p>}
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {items.map((opp) => <PipelineCard key={opp._id} opportunity={opp} onClick={() => navigate(`/funding/${opp._id}`)} />)}
                </div>
              </div>
            );
          })}
          <div className="w-64 flex flex-col bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Closed</span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {terminalStages.map((stage) => byStage(stage).map((opp) => <PipelineCard key={opp._id} opportunity={opp} onClick={() => navigate(`/funding/${opp._id}`)} />))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reports Dashboard ─────────────────────────────────────────────

function ReportsDashboard({ summary, onBack }: { summary: any; onBack: () => void }) {
  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0 flex items-center gap-4">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ArrowLeft size={18} /></button>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Funding Reports & Analytics</h1>
      </div>

      <div className="p-6 space-y-6 max-w-5xl">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Total Pipeline" value={fmt(summary.totalValue)} sub={`${summary.total} opportunities`} icon={<DollarSign size={18} />} color="text-blue-500" />
          <SummaryCard label="Win Rate" value={`${summary.winRate ?? 0}%`} sub={`${summary.awarded} won / ${summary.awarded + (summary.declined ?? 0)} closed`} icon={<TrendingUp size={18} />} color="text-purple-500" />
          <SummaryCard label="Avg Deal Size" value={fmt(summary.avgDealSize ?? 0)} sub="per opportunity" icon={<Target size={18} />} color="text-indigo-500" />
          <SummaryCard label="Budget Utilization" value={summary.budgetSummary?.totalBudgeted > 0 ? `${Math.round(((summary.budgetSummary?.totalActual ?? 0) / summary.budgetSummary.totalBudgeted) * 100)}%` : '—'} sub={`${fmt(summary.budgetSummary?.totalActual ?? 0)} / ${fmt(summary.budgetSummary?.totalBudgeted ?? 0)}`} icon={<BarChart3 size={18} />} color="text-green-500" />
        </div>

        {/* Pipeline by Stage - Bar Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Pipeline by Stage</h3>
          <div className="space-y-2">
            {Object.entries(STAGE_LABELS).map(([id, label]) => {
              const data = summary.byStage?.[id];
              if (!data) return null;
              const maxVal = Math.max(...Object.values(summary.byStage as Record<string, { value: number }>).map((v) => v.value), 1);
              const pct = (data.value / maxVal) * 100;
              return (
                <div key={id} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400 w-24 text-right">{label}</span>
                  <div className="flex-1 h-5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: STAGE_COLORS[id as FundingStage] }} />
                  </div>
                  <span className="text-xs text-gray-600 dark:text-gray-300 w-20">{fmt(data.value)}</span>
                  <span className="text-xs text-gray-400 w-8">{data.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* By Funder */}
        {Object.keys(summary.byFunder || {}).length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Top Funders</h3>
            <div className="space-y-2">
              {Object.entries(summary.byFunder as Record<string, { count: number; value: number; awarded: number }>)
                .sort(([, a], [, b]) => b.value - a.value)
                .slice(0, 10)
                .map(([name, data]) => (
                  <div key={name} className="flex items-center gap-3 text-sm">
                    <span className="text-gray-700 dark:text-gray-300 flex-1 truncate">{name}</span>
                    <span className="text-xs text-gray-400">{data.count} opp</span>
                    <span className="text-xs text-green-600 dark:text-green-400">{data.awarded} won</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-24 text-right">{fmt(data.value)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* By Funding Type */}
        {Object.keys(summary.byFundingType || {}).length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">By Funding Type</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(summary.byFundingType as Record<string, number>).map(([type, count]) => (
                <div key={type} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{FUNDING_TYPE_LABELS[type] || type}</span>
                  <span className="text-xs text-gray-400 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full">{count as number}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Deadlines */}
        {summary.upcomingDeadlines?.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-500" /> Upcoming Deadlines (30 days)</h3>
            <div className="space-y-2">
              {summary.upcomingDeadlines.map((d: any) => (
                <div key={d.id} className="flex items-center gap-3 text-sm">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_COLORS[d.stage as FundingStage] || '#94a3b8' }} />
                  <span className="text-gray-700 dark:text-gray-300 flex-1 truncate">{d.name}</span>
                  <span className="text-xs text-red-500 font-medium">{fmtDate(d.deadline)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compliance Due */}
        {summary.complianceDue?.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5"><Shield size={14} className="text-red-500" /> Compliance Due Soon</h3>
            <div className="space-y-2">
              {summary.complianceDue.map((c: any, i: number) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${c.status === 'overdue' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>{c.status}</span>
                  <span className="text-gray-700 dark:text-gray-300 flex-1 truncate">{c.title}</span>
                  <span className="text-xs text-gray-500 truncate">{c.fundingName}</span>
                  <span className="text-xs text-gray-400">{fmtDate(c.dueDate)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shared Cards ──────────────────────────────────────────────────

function SummaryCard({ label, value, sub, icon, color }: { label: string; value: string; sub: string; icon: React.ReactNode; color: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center gap-2 mb-1"><span className={color}>{icon}</span><span className="text-xs text-gray-500 dark:text-gray-400">{label}</span></div>
      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{value}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500">{sub}</p>
    </div>
  );
}

function PipelineCard({ opportunity: opp, onClick }: { opportunity: FundingOpportunity; onClick: () => void }) {
  return (
    <button onClick={onClick} className="w-full text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:shadow-md transition-shadow">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate mb-1">{opp.name}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-2">{opp.funderName}</p>
      <div className="flex items-center justify-between">
        {opp.amount > 0 && <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{fmt(opp.amount, opp.currency)}</span>}
        {opp.probability > 0 && <span className="text-xs text-gray-400">{opp.probability}%</span>}
      </div>
      {opp.deadlineDate && <p className="text-xs text-gray-400 mt-1">Due: {fmtDate(opp.deadlineDate)}</p>}
      {opp.owner && <div className="flex items-center gap-1 mt-1.5"><User size={10} className="text-gray-400" /><span className="text-xs text-gray-400 truncate">{opp.owner}</span></div>}
    </button>
  );
}

function InfoCard({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-1.5 mb-2 text-gray-500 dark:text-gray-400">{icon}<span className="text-xs font-medium">{label}</span></div>
      {children}
    </div>
  );
}

// ── Detail View ───────────────────────────────────────────────────

type DetailTab = 'overview' | 'budget' | 'timeline' | 'checklist' | 'compliance' | 'documents';

function FundingDetail({ opportunity: opp }: { opportunity: FundingOpportunity }) {
  const navigate = useNavigate();
  const { updateOpportunity, deleteOpportunity, fetchOpportunities, fetchSummary } = useFundingStore();
  const [tab, setTab] = useState<DetailTab>('overview');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<FundingOpportunity>>({});

  const handleDelete = async () => {
    if (!confirm('Delete this funding opportunity?')) return;
    try { await deleteOpportunity(opp._id); toast.success('Deleted'); navigate('/funding'); } catch (err: any) { toast.error(err.message); }
  };

  const handleStageChange = async (stage: string) => {
    try {
      await updateOpportunity(opp._id, { stage } as any);
      toast.success(`Stage → ${STAGE_LABELS[stage as FundingStage] || stage}`);
      if (stage === 'awarded') toast('You can now create a project from this opportunity!', { icon: '🎉' });
    } catch (err: any) { toast.error(err.message); }
  };

  const handleCreateProject = async () => {
    try {
      const project = await fundingApi.createProject(opp._id);
      toast.success(`Project "${project.name}" created`);
      fetchOpportunities();
      navigate(`/project/${project._id}`);
    } catch (err: any) { toast.error(err.message); }
  };

  const startEdit = () => {
    setEditForm({ name: opp.name, description: opp.description, funderName: opp.funderName, funderType: opp.funderType, funderWebsite: opp.funderWebsite, fundingType: opp.fundingType, amount: opp.amount, owner: opp.owner, priority: opp.priority, probability: opp.probability, deadlineDate: opp.deadlineDate, decisionDate: opp.decisionDate, startDate: opp.startDate, endDate: opp.endDate, category: opp.category, programArea: opp.programArea, eligibilityNotes: opp.eligibilityNotes, matchRequirement: opp.matchRequirement, notes: opp.notes });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    try { await updateOpportunity(opp._id, editForm); setEditing(false); toast.success('Updated'); fetchSummary(); } catch (err: any) { toast.error(err.message); }
  };

  const updateField = (f: string, v: any) => setEditForm((p) => ({ ...p, [f]: v }));

  const tabs: { id: DetailTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <DollarSign size={14} /> },
    { id: 'budget', label: 'Budget', icon: <Layers size={14} /> },
    { id: 'timeline', label: 'Multi-Year', icon: <Calendar size={14} /> },
    { id: 'checklist', label: 'Checklist', icon: <ClipboardCheck size={14} /> },
    { id: 'compliance', label: 'Compliance', icon: <Shield size={14} /> },
    { id: 'documents', label: 'Documents', icon: <FileText size={14} /> },
  ];

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4">
        <button onClick={() => navigate('/funding')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ArrowLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">{opp.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 text-xs font-medium rounded-full text-white" style={{ backgroundColor: STAGE_COLORS[opp.stage] }}>{STAGE_LABELS[opp.stage]}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{opp.funderName}</span>
            {opp.amount > 0 && <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{fmt(opp.amount, opp.currency)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {opp.stage === 'awarded' && !opp.projectId && (
            <button onClick={handleCreateProject} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors">
              <ExternalLink size={14} /> Create Project
            </button>
          )}
          {opp.projectId && (
            <button onClick={() => navigate(`/project/${opp.projectId}`)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-accent border border-accent rounded-lg hover:bg-accent/10">
              <ExternalLink size={14} /> View Project
            </button>
          )}
          <button onClick={startEdit} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><Pencil size={16} /></button>
          <button onClick={handleDelete} className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"><Trash2 size={16} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 border-b border-gray-200 dark:border-gray-700 flex gap-1 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? 'border-accent text-accent font-medium' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-6 max-w-4xl">
        {tab === 'overview' && <OverviewTab opp={opp} onStageChange={handleStageChange} />}
        {tab === 'budget' && <BudgetTab opp={opp} />}
        {tab === 'timeline' && <MultiYearTab opp={opp} />}
        {tab === 'checklist' && <ChecklistTab opp={opp} />}
        {tab === 'compliance' && <ComplianceTab opp={opp} />}
        {tab === 'documents' && <DocumentsTab opp={opp} />}
      </div>

      {editing && <EditModal form={editForm} updateField={updateField} onSave={handleSaveEdit} onClose={() => setEditing(false)} />}
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────

function OverviewTab({ opp, onStageChange }: { opp: FundingOpportunity; onStageChange: (s: string) => void }) {
  const { updateOpportunity } = useFundingStore();
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactForm, setContactForm] = useState<Partial<FundingContact>>({});

  const handleAddContact = async () => {
    if (!contactForm.name?.trim()) { toast.error('Name required'); return; }
    try {
      await updateOpportunity(opp._id, { contacts: [...(opp.contacts || []), { name: contactForm.name || '', title: contactForm.title || '', email: contactForm.email || '', phone: contactForm.phone || '', isPrimary: contactForm.isPrimary || false, notes: '' }] } as any);
      setContactForm({}); setShowAddContact(false); toast.success('Contact added');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRemoveContact = async (idx: number) => {
    const updated = [...(opp.contacts || [])]; updated.splice(idx, 1);
    try { await updateOpportunity(opp._id, { contacts: updated } as any); toast.success('Removed'); } catch (err: any) { toast.error(err.message); }
  };

  return (
    <div className="space-y-6">
      {/* Stage Progression */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Pipeline Stage</h3>
        <div className="flex flex-wrap gap-1.5">
          {(Object.entries(STAGE_LABELS) as [FundingStage, string][]).map(([id, label]) => (
            <button key={id} onClick={() => onStageChange(id)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${opp.stage === id ? 'text-white border-transparent' : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              style={opp.stage === id ? { backgroundColor: STAGE_COLORS[id] } : {}}>{label}</button>
          ))}
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoCard label="Funder" icon={<Building2 size={14} />}>
          <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{opp.funderName}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{FUNDER_TYPE_LABELS[opp.funderType] || opp.funderType}</p>
          {opp.funderWebsite && <a href={opp.funderWebsite} target="_blank" rel="noopener" className="text-xs text-accent hover:underline flex items-center gap-1 mt-1"><Globe size={10} /> Website</a>}
        </InfoCard>
        <InfoCard label="Funding Details" icon={<DollarSign size={14} />}>
          <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{fmt(opp.amount, opp.currency)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{FUNDING_TYPE_LABELS[opp.fundingType] || opp.fundingType}</p>
          {opp.matchRequirement > 0 && <p className="text-xs text-gray-500 mt-1">Match: {opp.matchRequirement}%</p>}
        </InfoCard>
        <InfoCard label="Owner & Team" icon={<User size={14} />}>
          <p className="text-sm text-gray-900 dark:text-gray-100">{opp.owner || '—'}</p>
          {opp.teamMembers?.length > 0 && <p className="text-xs text-gray-500 mt-1">Team: {opp.teamMembers.join(', ')}</p>}
        </InfoCard>
        <InfoCard label="Win Probability" icon={<TrendingUp size={14} />}>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${opp.probability}%`, backgroundColor: opp.probability >= 70 ? '#22c55e' : opp.probability >= 40 ? '#f59e0b' : '#ef4444' }} />
            </div>
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{opp.probability}%</span>
          </div>
        </InfoCard>
        <InfoCard label="Key Dates" icon={<Calendar size={14} />}>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Identified</span><span className="text-gray-900 dark:text-gray-100">{fmtDate(opp.identifiedDate)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Deadline</span><span className="text-gray-900 dark:text-gray-100 font-medium">{fmtDate(opp.deadlineDate)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Decision</span><span className="text-gray-900 dark:text-gray-100">{fmtDate(opp.decisionDate)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Period</span><span className="text-gray-900 dark:text-gray-100">{fmtDate(opp.startDate)} – {fmtDate(opp.endDate)}</span></div>
          </div>
        </InfoCard>
        <InfoCard label="Classification" icon={<Tag size={14} />}>
          <div className="space-y-1 text-xs">
            {opp.category && <div className="flex justify-between"><span className="text-gray-500">Category</span><span className="text-gray-900 dark:text-gray-100">{opp.category}</span></div>}
            {opp.programArea && <div className="flex justify-between"><span className="text-gray-500">Program Area</span><span className="text-gray-900 dark:text-gray-100">{opp.programArea}</span></div>}
            {opp.priority && <div className="flex justify-between"><span className="text-gray-500">Priority</span><span className="text-gray-900 dark:text-gray-100 capitalize">{opp.priority}</span></div>}
            {opp.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{opp.tags.map((t) => <span key={t} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">{t}</span>)}</div>}
          </div>
        </InfoCard>
      </div>

      {/* Text Sections */}
      {opp.description && <div><h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description</h3><p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{opp.description}</p></div>}
      {opp.eligibilityNotes && <div><h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Eligibility Notes</h3><p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{opp.eligibilityNotes}</p></div>}
      {opp.notes && <div><h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Notes</h3><p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{opp.notes}</p></div>}

      {/* Contacts */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Contacts</h3>
          <button onClick={() => setShowAddContact(true)} className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"><Plus size={12} /> Add</button>
        </div>
        {opp.contacts?.length > 0 ? (
          <div className="space-y-2">
            {opp.contacts.map((c, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.name}</p>
                    {c.isPrimary && <span className="text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded-full font-medium">Primary</span>}
                  </div>
                  {c.title && <p className="text-xs text-gray-500 dark:text-gray-400">{c.title}</p>}
                  <div className="flex items-center gap-3 mt-1">
                    {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-xs text-accent hover:underline"><Mail size={10} /> {c.email}</a>}
                    {c.phone && <span className="flex items-center gap-1 text-xs text-gray-500"><Phone size={10} /> {c.phone}</span>}
                  </div>
                </div>
                <button onClick={() => handleRemoveContact(idx)} className="p-1 text-gray-400 hover:text-red-500"><X size={14} /></button>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-gray-400">No contacts added.</p>}

        {showAddContact && (
          <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input type="text" placeholder="Name *" value={contactForm.name || ''} onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
              <input type="text" placeholder="Title" value={contactForm.title || ''} onChange={(e) => setContactForm((p) => ({ ...p, title: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
              <input type="email" placeholder="Email" value={contactForm.email || ''} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
              <input type="tel" placeholder="Phone" value={contactForm.phone || ''} onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400"><input type="checkbox" checked={contactForm.isPrimary || false} onChange={(e) => setContactForm((p) => ({ ...p, isPrimary: e.target.checked }))} className="rounded border-gray-300" /> Primary</label>
              <div className="flex-1" />
              <button onClick={() => setShowAddContact(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
              <button onClick={handleAddContact} className="text-xs bg-accent text-white px-3 py-1 rounded hover:bg-accent/90">Add</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Budget Tab ────────────────────────────────────────────────────

function BudgetTab({ opp }: { opp: FundingOpportunity }) {
  const { updateOpportunity } = useFundingStore();
  const items = opp.budgetItems || [];
  const [newItem, setNewItem] = useState({ category: 'personnel', description: '', amount: '', actual: '' });

  const totalBudget = items.reduce((s, i) => s + (i.amount || 0), 0);
  const totalActual = items.reduce((s, i) => s + (i.actual || 0), 0);
  const totalWithIndirect = totalBudget + (opp.indirectAmount || 0);

  const handleAdd = async () => {
    if (!newItem.description.trim()) { toast.error('Description required'); return; }
    const item: BudgetLineItem = { id: uid(), category: newItem.category, description: newItem.description, amount: parseFloat(newItem.amount) || 0, actual: parseFloat(newItem.actual) || 0, notes: '' };
    try {
      await updateOpportunity(opp._id, { budgetItems: [...items, item] } as any);
      setNewItem({ category: 'personnel', description: '', amount: '', actual: '' });
      toast.success('Line item added');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRemove = async (id: string) => {
    try { await updateOpportunity(opp._id, { budgetItems: items.filter((i) => i.id !== id) } as any); toast.success('Removed'); } catch (err: any) { toast.error(err.message); }
  };

  const handleActualUpdate = async (id: string, actual: number) => {
    const updated = items.map((i) => i.id === id ? { ...i, actual } : i);
    try { await updateOpportunity(opp._id, { budgetItems: updated } as any); } catch (err: any) { toast.error(err.message); }
  };

  const updateIndirect = async (rate: number) => {
    const indirectAmount = totalBudget * (rate / 100);
    try { await updateOpportunity(opp._id, { indirectRate: rate, indirectAmount: Math.round(indirectAmount) } as any); } catch { /* silent */ }
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs text-gray-500">Total Budget</p>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmt(totalWithIndirect)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs text-gray-500">Actual Spent</p>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmt(totalActual)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs text-gray-500">Remaining</p>
          <p className={`text-lg font-bold ${totalWithIndirect - totalActual >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(totalWithIndirect - totalActual)}</p>
        </div>
      </div>

      {/* Utilization bar */}
      {totalWithIndirect > 0 && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Budget Utilization</span>
            <span>{Math.round((totalActual / totalWithIndirect) * 100)}%</span>
          </div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min((totalActual / totalWithIndirect) * 100, 100)}%`, backgroundColor: (totalActual / totalWithIndirect) > 0.9 ? '#ef4444' : (totalActual / totalWithIndirect) > 0.7 ? '#f59e0b' : '#22c55e' }} />
          </div>
        </div>
      )}

      {/* Line Items */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Budget Line Items</h3>
        {items.length > 0 && (
          <div className="space-y-2 mb-4">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">{BUDGET_CATEGORIES[item.category] || item.category}</span>
                    <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{item.description}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-xs text-gray-500">Budget: {fmt(item.amount)}</span>
                    <span className="text-xs text-gray-500">Actual: </span>
                    <input type="number" value={item.actual || ''} onChange={(e) => handleActualUpdate(item.id, parseFloat(e.target.value) || 0)} className="w-24 px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" placeholder="0" />
                  </div>
                </div>
                <button onClick={() => handleRemove(item.id)} className="p-1 text-gray-400 hover:text-red-500"><X size={14} /></button>
              </div>
            ))}
          </div>
        )}

        {/* Add line item */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="grid grid-cols-4 gap-2">
            <select value={newItem.category} onChange={(e) => setNewItem((p) => ({ ...p, category: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent">
              {Object.entries(BUDGET_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input type="text" placeholder="Description" value={newItem.description} onChange={(e) => setNewItem((p) => ({ ...p, description: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
            <input type="number" placeholder="Amount" value={newItem.amount} onChange={(e) => setNewItem((p) => ({ ...p, amount: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
            <button onClick={handleAdd} className="px-3 py-1.5 text-sm bg-accent text-white rounded hover:bg-accent/90"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* Indirect Costs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Indirect Costs (F&A)</h4>
        <div className="flex items-center gap-3">
          <input type="number" value={opp.indirectRate || ''} onChange={(e) => updateIndirect(parseFloat(e.target.value) || 0)} className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" placeholder="0" />
          <span className="text-sm text-gray-500">%</span>
          <span className="text-sm text-gray-400">=</span>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{fmt(opp.indirectAmount || 0)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Multi-Year Tab ────────────────────────────────────────────────

function MultiYearTab({ opp }: { opp: FundingOpportunity }) {
  const { updateOpportunity } = useFundingStore();
  const allocs = opp.yearlyAllocations || [];
  const [newYear, setNewYear] = useState(String(new Date().getFullYear()));
  const [newAmount, setNewAmount] = useState('');

  const totalPlanned = allocs.reduce((s, a) => s + (a.amount || 0), 0);
  const totalActual = allocs.reduce((s, a) => s + (a.actual || 0), 0);

  const handleAdd = async () => {
    const yr = parseInt(newYear);
    if (!yr || yr < 2000 || yr > 2100) { toast.error('Valid year required'); return; }
    if (allocs.some((a) => a.year === yr)) { toast.error('Year already exists'); return; }
    const alloc: YearlyAllocation = { year: yr, amount: parseFloat(newAmount) || 0, actual: 0, status: 'planned', notes: '' };
    try {
      const updated = [...allocs, alloc].sort((a, b) => a.year - b.year);
      await updateOpportunity(opp._id, { yearlyAllocations: updated } as any);
      setNewAmount(''); toast.success('Year added');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleUpdate = async (year: number, field: string, value: any) => {
    const updated = allocs.map((a) => a.year === year ? { ...a, [field]: value } : a);
    try { await updateOpportunity(opp._id, { yearlyAllocations: updated } as any); } catch (err: any) { toast.error(err.message); }
  };

  const handleRemove = async (year: number) => {
    try { await updateOpportunity(opp._id, { yearlyAllocations: allocs.filter((a) => a.year !== year) } as any); toast.success('Removed'); } catch (err: any) { toast.error(err.message); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3"><p className="text-xs text-gray-500">Total Planned</p><p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmt(totalPlanned)}</p></div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3"><p className="text-xs text-gray-500">Total Disbursed</p><p className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmt(totalActual)}</p></div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3"><p className="text-xs text-gray-500">Years</p><p className="text-lg font-bold text-gray-900 dark:text-gray-100">{allocs.length}</p></div>
      </div>

      {allocs.length > 0 && (
        <div className="space-y-2">
          {allocs.map((a) => (
            <div key={a.year} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <span className="text-sm font-bold text-gray-900 dark:text-gray-100 w-12">{a.year}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500">Planned:</span>
                  <input type="number" value={a.amount || ''} onChange={(e) => handleUpdate(a.year, 'amount', parseFloat(e.target.value) || 0)} className="w-24 px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
                  <span className="text-xs text-gray-500">Actual:</span>
                  <input type="number" value={a.actual || ''} onChange={(e) => handleUpdate(a.year, 'actual', parseFloat(e.target.value) || 0)} className="w-24 px-1.5 py-0.5 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
                </div>
                {a.amount > 0 && (
                  <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${Math.min((a.actual / a.amount) * 100, 100)}%` }} />
                  </div>
                )}
              </div>
              <select value={a.status} onChange={(e) => handleUpdate(a.year, 'status', e.target.value)} className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded">
                <option value="planned">Planned</option><option value="approved">Approved</option><option value="disbursed">Disbursed</option><option value="closed">Closed</option>
              </select>
              <button onClick={() => handleRemove(a.year)} className="p-1 text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input type="number" placeholder="Year" value={newYear} onChange={(e) => setNewYear(e.target.value)} className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
        <input type="number" placeholder="Amount" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} className="w-32 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
        <button onClick={handleAdd} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-accent text-white rounded hover:bg-accent/90"><Plus size={14} /> Add Year</button>
      </div>
    </div>
  );
}

// ── Checklist Tab ─────────────────────────────────────────────────

function ChecklistTab({ opp }: { opp: FundingOpportunity }) {
  const { updateOpportunity } = useFundingStore();
  const items = opp.checklist || [];
  const [newText, setNewText] = useState('');
  const [newStage, setNewStage] = useState<string>(opp.stage || 'preparing');

  const handleAdd = async () => {
    if (!newText.trim()) return;
    const item: ChecklistItem = { id: uid(), text: newText.trim(), completed: false, stage: newStage, completedAt: '', completedBy: '', dueDate: '' };
    try { await updateOpportunity(opp._id, { checklist: [...items, item] } as any); setNewText(''); toast.success('Added'); } catch (err: any) { toast.error(err.message); }
  };

  const handleToggle = async (id: string) => {
    const updated = items.map((i) => i.id === id ? { ...i, completed: !i.completed, completedAt: !i.completed ? new Date().toISOString() : '' } : i);
    try { await updateOpportunity(opp._id, { checklist: updated } as any); } catch (err: any) { toast.error(err.message); }
  };

  const handleRemove = async (id: string) => {
    try { await updateOpportunity(opp._id, { checklist: items.filter((i) => i.id !== id) } as any); } catch (err: any) { toast.error(err.message); }
  };

  const completed = items.filter((i) => i.completed).length;
  const total = items.length;

  // Group by stage
  const stages = [...new Set(items.map((i) => i.stage || 'general'))];

  return (
    <div className="space-y-6">
      {/* Progress */}
      {total > 0 && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Progress</span><span>{completed}/{total} ({Math.round((completed / total) * 100)}%)</span></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${(completed / total) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Items grouped by stage */}
      {stages.map((stage) => {
        const stageItems = items.filter((i) => (i.stage || 'general') === stage);
        return (
          <div key={stage}>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage as FundingStage] || '#94a3b8' }} />
              {STAGE_LABELS[stage as FundingStage] || stage}
            </h4>
            <div className="space-y-1">
              {stageItems.map((item) => (
                <div key={item.id} className="flex items-center gap-3 py-1.5 group">
                  <button onClick={() => handleToggle(item.id)} className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${item.completed ? 'bg-accent border-accent text-white' : 'border-gray-300 dark:border-gray-600 hover:border-accent'}`}>
                    {item.completed && <Check size={12} />}
                  </button>
                  <span className={`text-sm flex-1 ${item.completed ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>{item.text}</span>
                  {item.completedAt && <span className="text-[10px] text-gray-400">{fmtDate(item.completedAt)}</span>}
                  <button onClick={() => handleRemove(item.id)} className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500"><X size={12} /></button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Add new */}
      <div className="flex items-center gap-2">
        <select value={newStage} onChange={(e) => setNewStage(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent">
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input type="text" placeholder="New checklist item..." value={newText} onChange={(e) => setNewText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
        <button onClick={handleAdd} className="px-3 py-1.5 text-sm bg-accent text-white rounded hover:bg-accent/90"><Plus size={14} /></button>
      </div>
    </div>
  );
}

// ── Compliance Tab ────────────────────────────────────────────────

function ComplianceTab({ opp }: { opp: FundingOpportunity }) {
  const { updateOpportunity } = useFundingStore();
  const reqs = opp.complianceRequirements || [];
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: 'quarterly-report', title: '', dueDate: '', assignee: '' });

  const handleAdd = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    const req: ComplianceRequirement = { id: uid(), type: form.type, title: form.title, dueDate: form.dueDate, status: 'pending', submittedDate: '', assignee: form.assignee, notes: '', linkedDeliverables: [] };
    try { await updateOpportunity(opp._id, { complianceRequirements: [...reqs, req] } as any); setForm({ type: 'quarterly-report', title: '', dueDate: '', assignee: '' }); setShowAdd(false); toast.success('Added'); } catch (err: any) { toast.error(err.message); }
  };

  const handleStatusChange = async (id: string, status: string) => {
    const updated = reqs.map((r) => r.id === id ? { ...r, status, submittedDate: status === 'submitted' ? new Date().toISOString().split('T')[0] : r.submittedDate } : r);
    try { await updateOpportunity(opp._id, { complianceRequirements: updated } as any); toast.success('Updated'); } catch (err: any) { toast.error(err.message); }
  };

  const handleRemove = async (id: string) => {
    try { await updateOpportunity(opp._id, { complianceRequirements: reqs.filter((r) => r.id !== id) } as any); toast.success('Removed'); } catch (err: any) { toast.error(err.message); }
  };

  const statusColors: Record<string, string> = { pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', 'in-progress': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', submitted: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };

  const pendingCount = reqs.filter((r) => r.status === 'pending' || r.status === 'overdue').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Compliance & Reporting Requirements</h3>
          {pendingCount > 0 && <p className="text-xs text-amber-500 mt-0.5">{pendingCount} items need attention</p>}
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"><Plus size={12} /> Add Requirement</button>
      </div>

      {showAdd && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent">
              {Object.entries(COMPLIANCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input type="text" placeholder="Title *" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
            <input type="date" value={form.dueDate} onChange={(e) => setForm((p) => ({ ...p, dueDate: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
            <input type="text" placeholder="Assignee" value={form.assignee} onChange={(e) => setForm((p) => ({ ...p, assignee: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
            <button onClick={handleAdd} className="text-xs bg-accent text-white px-3 py-1 rounded hover:bg-accent/90">Add</button>
          </div>
        </div>
      )}

      {reqs.length > 0 ? (
        <div className="space-y-2">
          {reqs.map((r) => (
            <div key={r.id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusColors[r.status] || statusColors.pending}`}>{r.status}</span>
                  <span className="text-xs text-gray-400">{COMPLIANCE_TYPES[r.type] || r.type}</span>
                </div>
                <p className="text-sm text-gray-900 dark:text-gray-100">{r.title}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  {r.dueDate && <span>Due: {fmtDate(r.dueDate)}</span>}
                  {r.assignee && <span>Assignee: {r.assignee}</span>}
                  {r.submittedDate && <span>Submitted: {fmtDate(r.submittedDate)}</span>}
                </div>
              </div>
              <select value={r.status} onChange={(e) => handleStatusChange(r.id, e.target.value)} className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent">
                <option value="pending">Pending</option><option value="in-progress">In Progress</option><option value="submitted">Submitted</option><option value="approved">Approved</option><option value="overdue">Overdue</option>
              </select>
              <button onClick={() => handleRemove(r.id)} className="p-1 text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-gray-400">No compliance requirements defined.</p>}
    </div>
  );
}

// ── Documents Tab ─────────────────────────────────────────────────

function DocumentsTab({ opp }: { opp: FundingOpportunity }) {
  const [docs, setDocs] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState('general');

  useEffect(() => { loadDocs(); }, [opp._id]);

  const loadDocs = async () => {
    try { const list = await fundingApi.getDocuments(opp._id); setDocs(list); } catch { /* empty */ }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      await fundingApi.uploadDocument(opp._id, file, { documentType: docType });
      toast.success('Document uploaded');
      await loadDocs();
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const DOC_TYPE_LABELS: Record<string, string> = {
    general: 'General', proposal: 'Proposal', budget: 'Budget', loi: 'Letter of Intent',
    narrative: 'Narrative', 'support-letter': 'Support Letter', compliance: 'Compliance',
    report: 'Report', contract: 'Contract', other: 'Other',
  };

  function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div className="space-y-6">
      {/* Upload */}
      <div className="flex items-center gap-3">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent">
          {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden" />
        <button onClick={() => fileInputRef.current?.click()} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50">
          <Upload size={14} /> {loading ? 'Uploading...' : 'Upload Document'}
        </button>
      </div>

      {/* Document List */}
      {docs.length > 0 ? (
        <div className="space-y-2">
          {docs.map((doc) => (
            <div key={doc._id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="w-8 h-8 rounded bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-500"><FolderOpen size={16} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{doc.filename}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                  <span>{DOC_TYPE_LABELS[doc.documentType || ''] || doc.documentType || 'General'}</span>
                  <span>&middot;</span>
                  <span>{formatSize(doc.size)}</span>
                  <span>&middot;</span>
                  <span>{fmtDate(doc.createdAt)}</span>
                  {doc.approvalStatus && doc.approvalStatus !== 'draft' && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${doc.approvalStatus === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>{doc.approvalStatus}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => void downloadFile(`/attachments/${doc._id}/download`, doc.filename || 'attachment')}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500"
                title="Download"
              ><ChevronRight size={14} /></button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
          <FileText size={32} className="mb-2" />
          <p className="text-sm">No documents uploaded yet</p>
          <p className="text-xs mt-1">Upload proposals, budgets, LOIs, and more</p>
        </div>
      )}
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────

function EditModal({ form, updateField, onSave, onClose }: {
  form: Partial<FundingOpportunity>; updateField: (f: string, v: any) => void; onSave: () => void; onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Opportunity</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X size={16} className="text-gray-500" /></button>
        </div>
        <div className="space-y-3">
          <EF label="Name" value={form.name || ''} onChange={(v) => updateField('name', v)} />
          <EF label="Description" value={form.description || ''} onChange={(v) => updateField('description', v)} multiline />
          <EF label="Funder Name" value={form.funderName || ''} onChange={(v) => updateField('funderName', v)} />
          <EF label="Website" value={form.funderWebsite || ''} onChange={(v) => updateField('funderWebsite', v)} />
          <div className="grid grid-cols-2 gap-3">
            <ES label="Funder Type" value={form.funderType || 'other'} onChange={(v) => updateField('funderType', v)} options={[['federal','Federal'],['state','State'],['local','Local'],['foundation','Foundation'],['corporate','Corporate'],['nonprofit','Nonprofit'],['international','International'],['other','Other']]} />
            <ES label="Funding Type" value={form.fundingType || 'grant'} onChange={(v) => updateField('fundingType', v)} options={[['grant','Grant'],['contract','Contract'],['cooperative-agreement','Coop Agree.'],['subgrant','Subgrant'],['sponsorship','Sponsorship'],['donation','Donation'],['loan','Loan'],['venture','Venture'],['other','Other']]} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <EF label="Amount" value={String(form.amount || '')} onChange={(v) => updateField('amount', parseFloat(v) || 0)} type="number" />
            <EF label="Probability (%)" value={String(form.probability || '')} onChange={(v) => updateField('probability', parseInt(v) || 0)} type="number" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <EF label="Owner" value={form.owner || ''} onChange={(v) => updateField('owner', v)} />
            <ES label="Priority" value={form.priority || 'medium'} onChange={(v) => updateField('priority', v)} options={[['low','Low'],['medium','Medium'],['high','High'],['critical','Critical']]} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <EF label="Deadline" value={form.deadlineDate || ''} onChange={(v) => updateField('deadlineDate', v)} type="date" />
            <EF label="Decision Date" value={form.decisionDate || ''} onChange={(v) => updateField('decisionDate', v)} type="date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <EF label="Start Date" value={form.startDate || ''} onChange={(v) => updateField('startDate', v)} type="date" />
            <EF label="End Date" value={form.endDate || ''} onChange={(v) => updateField('endDate', v)} type="date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <EF label="Category" value={form.category || ''} onChange={(v) => updateField('category', v)} />
            <EF label="Program Area" value={form.programArea || ''} onChange={(v) => updateField('programArea', v)} />
          </div>
          <EF label="Match Req (%)" value={String(form.matchRequirement || '')} onChange={(v) => updateField('matchRequirement', parseFloat(v) || 0)} type="number" />
          <EF label="Eligibility Notes" value={form.eligibilityNotes || ''} onChange={(v) => updateField('eligibilityNotes', v)} multiline />
          <EF label="Notes" value={form.notes || ''} onChange={(v) => updateField('notes', v)} multiline />
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
          <button onClick={onSave} className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90">Save</button>
        </div>
      </div>
    </div>
  );
}

function EF({ label, value, onChange, type = 'text', multiline = false }: { label: string; value: string; onChange: (v: string) => void; type?: string; multiline?: boolean }) {
  const c = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50";
  return <div><label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>{multiline ? <textarea value={value} onChange={(e) => onChange(e.target.value)} className={c} rows={2} /> : <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={c} />}</div>;
}

function ES({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return <div><label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label><select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50">{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>;
}
